import { useEffect, useState } from 'react';
import { APP_NAME, IDLE_AFTER_MS } from '@clanfire/shared';
import { getSocket } from '@/realtime/socket';
import { useNotifications, useReadStates, useConversations } from '@/stores/social';
import { useConnection, useTyping } from '@/stores/realtime';

/** Reports idle/active to the server so presence can show "Idle" automatically. */
export function useIdleReporter() {
  const status = useConnection((s) => s.status);
  useEffect(() => {
    if (status !== 'connected') return;
    let idle = false;
    let timer: number;
    const report = (value: boolean) => {
      if (idle === value) return;
      idle = value;
      getSocket()?.emit('presence:idle', { idle: value });
    };
    const activity = () => {
      window.clearTimeout(timer);
      report(false);
      timer = window.setTimeout(() => report(true), IDLE_AFTER_MS);
    };
    const events = ['mousemove', 'keydown', 'pointerdown', 'focus'] as const;
    events.forEach((e) => window.addEventListener(e, activity, { passive: true }));
    activity();
    return () => {
      window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, activity));
    };
  }, [status]);
}

/** "(3) ClanFire" when there are unread mentions or DMs. */
export function useDocumentTitle() {
  const mentions = useReadStates((s) => Object.values(s.channels).reduce((sum, c) => sum + c.mentionCount, 0));
  const dms = useConversations((s) => Object.values(s.conversations).reduce((sum, c) => sum + c.unreadCount, 0));
  const friendRequests = useNotifications((s) => s.items.filter((n) => n.type === 'FRIEND_REQUEST' && !n.readAt).length);
  useEffect(() => {
    const total = mentions + dms + friendRequests;
    document.title = total > 0 ? `(${total > 99 ? '99+' : total}) ${APP_NAME}` : APP_NAME;
  }, [mentions, dms, friendRequests]);
}

/** Periodically drops expired "is typing" entries. */
export function useTypingPruner() {
  useEffect(() => {
    const id = window.setInterval(() => useTyping.getState().prune(), 1000);
    return () => window.clearInterval(id);
  }, []);
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    onChange();
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/** Tailwind `md` breakpoint and up (tablet/desktop). */
export const useIsDesktop = () => useMediaQuery('(min-width: 768px)');
/** Wide enough to show the member list as a column. */
export const useIsWide = () => useMediaQuery('(min-width: 1200px)');
